import { queryOptions } from '@tanstack/react-query';
import {
  addWeeks,
  eachWeekOfInterval,
  endOfWeek,
  format,
  isAfter,
  max,
  min,
  parseISO,
} from 'date-fns';

import { BRAND_SERVICE_VERSION_HEADERS } from '@/constants/environments';
import apiClient from '@/constants/http';
import {
  isNoResponse,
  retryOnServerError,
  throwOnHttpError,
} from '@/utils/apiErrors';
import { Logger } from '@/utils/logging';

import type {
  BarberScheduleResponse,
  UseGuestScheduleQueriesParams,
} from './types';

const API_DATE_FORMAT = 'yyyy-MM-dd';
const MAX_WEEKS_IN_REQUEST = 4;

interface FetchInterval {
  start: string;
  end: string;
}

/**
 * Splits a date range into intervals of max N weeks each.
 * This matches WOW mobile's approach for better API performance.
 */
function getScheduleFetchIntervals(
  startDateISO: string,
  endDateISO: string,
  maxAllowedWeeks: number,
): FetchInterval[] {
  const weekStartsOn = 0; // Sunday - sync requests across all locales
  const weeksToAdd = Math.max(maxAllowedWeeks - 1, 0);
  const reservationInterval = {
    start: parseISO(startDateISO),
    end: parseISO(endDateISO),
  };

  return eachWeekOfInterval(reservationInterval, { weekStartsOn })
    .filter((_, idx) => idx % maxAllowedWeeks === 0) // every N-th week
    .map((week, idx, arr) => {
      const firstWeek = week;
      const intervalStart =
        idx === 0 ? max([firstWeek, reservationInterval.start]) : firstWeek;

      const lastWeek = endOfWeek(addWeeks(week, weeksToAdd), { weekStartsOn });
      const intervalEnd =
        idx === arr.length - 1
          ? min([lastWeek, reservationInterval.end])
          : lastWeek;

      if (isAfter(intervalStart, intervalEnd)) {
        throw new Error('Negative interval'); // This should never happen
      }

      return {
        start: format(intervalStart, API_DATE_FORMAT),
        end: format(intervalEnd, API_DATE_FORMAT),
      };
    });
}

async function fetchBarberSchedule({
  barberId,
  shopId,
  startDate,
  endDate,
  serviceDuration,
  serviceId,
}: {
  barberId: string | null;
  shopId: string;
  startDate: string;
  endDate: string;
  serviceDuration: number;
  serviceId: string | null;
}): Promise<BarberScheduleResponse[]> {
  // Split the date range into 4-week chunks (like WOW mobile)
  const intervals = getScheduleFetchIntervals(
    startDate,
    endDate,
    MAX_WEEKS_IN_REQUEST,
  );

  // Shop endpoint requires the brand-service version header to accept serviceId
  const headers = !barberId ? BRAND_SERVICE_VERSION_HEADERS : undefined;

  // Make parallel requests for each interval
  const requests = intervals.map(({ start, end }) => {
    // Barber endpoint uses serviceDuration
    // Shop endpoint uses serviceId
    const endpoint = barberId
      ? `/v1/barber/${barberId}/schedule-time-range/${start}/${end}/${serviceDuration}`
      : `/v1/shop/${shopId}/schedule-time-range/${start}/${end}${serviceId ? `/${serviceId}` : ''}`;

    // Resolve every status but 401 so the status survives the api client's
    // interceptor (it rejects with the body alone) and `retry` can act on it.
    // 401 still rejects, leaving the refresh-and-replay path untouched. Local
    // to this call on purpose — see `throwOnHttpError`.
    return apiClient
      .get<BarberScheduleResponse[]>(endpoint, {
        headers,
        validateStatus: (status) => status !== 401,
      })
      .then(throwOnHttpError);
  });

  // Execute all requests in parallel and combine results
  try {
    const responses = await Promise.all(requests);
    return responses.reduce<BarberScheduleResponse[]>(
      (acc, response) => acc.concat(response.data),
      [],
    );
  } catch (error) {
    // This fetch failing was previously silent client-side: no UI error state
    // reads it (an errored guest just renders as "no availability"), and
    // nothing reported it to Sentry/Datadog — only backend request logs saw it.
    const log = isNoResponse(error) ? Logger.warn : Logger.error;
    log('Error fetching booking schedule', {
      error,
      shopId,
      barberId,
      serviceId,
    });
    throw error;
  }
}

export const getBookingScheduleQuery = (
  params: UseGuestScheduleQueriesParams,
) => {
  const { barberId, serviceDuration, serviceId, shopId, startDate, endDate } =
    params;

  return queryOptions({
    queryKey: [
      'barber-schedule',
      barberId || 'any',
      startDate,
      endDate,
      barberId ? serviceDuration : serviceId,
    ],
    queryFn: () =>
      fetchBarberSchedule({
        barberId,
        shopId,
        startDate,
        endDate,
        serviceDuration,
        serviceId,
      }),
    // Hold off until the request can describe what to look up: a barber (barber
    // endpoint) or at least one service (shop endpoint). Note the shop endpoint
    // does NOT itself require a service — a service-less request returns 200 —
    // so this guard is about not firing before anything is selected, not about
    // avoiding the 400 SQR-14412 set out to remove.
    enabled: Boolean(barberId) || Boolean(serviceId),
    // Short cache so returning to Choose Time does not refetch all availability.
    // A taken slot is still caught: a rejected upsert refetches the schedule.
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: retryOnServerError,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    retryDelay: (attemptIndex: number) =>
      Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};
